/**
 * steps.js — the agent-creation flow's step table + tiny shared helpers.
 * Nothing here renders. Every step component under ./steps/ imports from here.
 */
import { authFetch } from '../../utils/api';
import { KNOWLEDGE_LIMIT_OVERRIDES } from '../../components/knowledgeOverview.js';
import { wixUpgradeUrl } from '../../utils/wixBilling';
import { navigateExternal } from '../../utils/navigation';

export const API = process.env.REACT_APP_API_BASE_URL;

/** Ordered steps. `key` is the URL segment: /agents/new/:botId/:key */
export const STEPS = [
  { key: 'start',     labelKey: 'ac_step_start',     width: 720 },
  { key: 'knowledge', labelKey: 'ac_step_knowledge', width: 880 },
  { key: 'design',    labelKey: 'ac_step_design',    width: 1360 },
  { key: 'notifications', labelKey: 'ac_step_notifications', width: 760 },
  { key: 'test',      labelKey: 'ac_step_test',      width: 920 },
  { key: 'channels',  labelKey: 'ac_step_channels',  width: 880 },
  { key: 'plan',      labelKey: 'ac_step_plan',      width: 1160 },
];
export const DONE_KEY = 'done';
export const stepIndex = (key) => STEPS.findIndex((s) => s.key === key);

/** Agent languages the Start step offers. `value` is what the backend expects. */
export const LANGUAGES = [
  { value: 'English',    label: 'English' },
  { value: 'German',     label: 'Deutsch' },
  { value: 'French',     label: 'Français' },
  { value: 'Spanish',    label: 'Español' },
  { value: 'Italian',    label: 'Italiano' },
  { value: 'Portuguese', label: 'Português' },
  { value: 'Dutch',      label: 'Nederlands' },
  { value: 'Polish',     label: 'Polski' },
];

/** Same outlined-field look as the settings pages (liveChatSettings TEXT_FIELD_SX). */
export const FIELD_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff' },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': { border: '1px solid #515151' },
  '& .MuiInputLabel-root': { color: 'gray' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#5616ea' },
  '& .MuiFormHelperText-root': { marginLeft: 0 },
};

/** GET the agent incl. plan + knowledge_data; null when missing / not ours. */
export async function fetchAgent(botId) {
  if (!botId) return null;
  try {
    const r = await fetch(`${API}bot?bot_id=${encodeURIComponent(botId)}&include_plan=true`);
    if (!r.ok) return null;
    return await r.json();
  } catch { return null; }
}

/** Persist the per-user resume pointer { step (1-based), bot_id }. Best-effort. */
export function saveProgress(stepIdx, botId) {
  if (!botId) return Promise.resolve();
  return authFetch(`${API}onboarding/progress`, {
    method: 'PUT',
    body: JSON.stringify({ step: stepIdx + 1, bot_id: botId }),
  }).catch(() => {});
}

/** Plan name helper — 'Free' is the sentinel everywhere in the portal. */
export const planName = (agent) => agent?.subscription_plan?.name || 'Free';

/**
 * Knowledge usage — the SAME numbers the Knowledge Base shows: failed items
 * don't count; products and Wix CMS rows are managed elsewhere and don't
 * count; pages/links, documents and connector files share ONE pool
 * (the plan's document_limit), with the KB's grandfathered per-agent overrides.
 */
export function knowledgeUsage(agent) {
  const items = agent?.knowledge_data ?? [];
  const count = items.filter((i) => i.status !== 'failed' && i.type !== 'product' && i.type !== 'cms').length;
  const limit = KNOWLEDGE_LIMIT_OVERRIDES[agent?.id] ?? agent?.subscription_plan?.document_limit ?? 50;
  return { items, count, limit, atLimit: count >= limit, remaining: Math.max(0, limit - count) };
}

/** Stripe checkout for a plan row; success AND cancel return to `returnPath`
 *  inside the flow (the backend appends ?session_id= on success). */
export async function startCheckout({ agent, plan, returnPath }) {
  // New acquisition flows reuse these steps, including their upgrade modals.
  // Their Wix installation remains the billing authority throughout onboarding.
  if (agent.wix_product) {
    const status = await authFetch(`${API}wix/status?bot_id=${encodeURIComponent(agent.id)}`);
    if (!status.ok) throw new Error('Could not load Wix billing');
    navigateExternal(wixUpgradeUrl(await status.json()));
    return;
  }
  const res = await authFetch(`${API}create-checkout-session`, {
    method: 'POST',
    body: JSON.stringify({
      bot_id: agent.id,
      price_id: plan.stripe_subscription_plan_id,
      subscription_plan_id: plan.id,
      coupon_id: plan.coupon_id,
      success_url: `${window.location.origin}${returnPath}`,
      cancel_url: `${window.location.origin}${returnPath}`,
    }),
  });
  const data = await res.json();
  if (!data?.checkout_url) throw new Error('no checkout url');
  if (window.dataLayer) window.dataLayer.push({ event: 'checkout_session_started' });
  window.location.href = data.checkout_url;
}

/** Subscription plans, fetched once per session (PlanModal + the Plan step). */
let plansCache = null;
export async function fetchPlans() {
  if (plansCache) return plansCache;
  try {
    const r = await fetch(`${API}subscription_plans`);
    const j = r.ok ? await r.json() : [];
    plansCache = Array.isArray(j) ? j : [];
  } catch { plansCache = []; }
  return plansCache;
}
